import { Search, MoreHorizontal, Shield, Smartphone, Eye, Users, Megaphone, FileCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback, useRef } from 'react'
import Avatar from './ui/Avatar'
import api from '../services/api'
import { trackAppDownloadClick } from '../services/adTracking'

const PROMO_CARDS = [
  {
    icon: Shield,
    iconColor: '#1DA1F2',
    title: 'Trust what you see.',
    body: 'Every photo & video on OnRecord is stamped with time, location, and device proof. No edits. No fakes. Just truth.',
    cta: 'Get the App',
  },
  {
    icon: Eye,
    iconColor: '#F59E0B',
    title: 'In the age of deepfakes, seeing isn\'t believing.',
    body: 'OnRecord is. Every post is cryptographically verified at capture. Because the world deserves proof, not pixels.',
    cta: 'Download Free',
  },
  {
    icon: Megaphone,
    iconColor: '#EF4444',
    title: 'Record what matters.',
    body: 'Potholes, pollution, broken promises — put it on record. Your city won\'t fix what it can\'t see. Be the proof.',
    cta: 'Start Recording',
  },
  {
    icon: Users,
    iconColor: '#10B981',
    title: 'Inform, so no one can misinform.',
    body: 'You\'re aware — now make people be-ware. Share verified ground reports that algorithms can\'t bury.',
    cta: 'Join the Movement',
  },
  {
    icon: FileCheck,
    iconColor: '#8B5CF6',
    title: 'Your report. Your record. Your proof.',
    body: 'Journalists, activists, citizens — capture evidence that holds up. Timestamped. Geotagged. Tamper-proof.',
    cta: 'Get OnRecord',
  },
  {
    icon: Smartphone,
    iconColor: '#EC4899',
    title: 'Take matters into your own hands.',
    body: 'Literally. Your phone is now a verified witness. Download OnRecord and turn everyday moments into undeniable evidence.',
    cta: 'Download Now',
  },
]

interface Suggestion {
  id: string
  name: string
  username: string
  avatar?: string
  verified: boolean
  isFollowing: boolean
}

export default function RightSidebar() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [promoIndex, setPromoIndex] = useState(0)
  const [nextPromoIndex, setNextPromoIndex] = useState<number | null>(null)
  const [sliding, setSliding] = useState(false)
  const [showPromo, setShowPromo] = useState(false)
  const promoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Show promo to new users only — first 5 sessions, then hide until cards update
  useEffect(() => {
    const PROMO_VERSION = 'v3' // Bump this when adding new cards to re-show
    const key = `onrecord_promo_${PROMO_VERSION}`
    const data = JSON.parse(localStorage.getItem(key) || '{"views":0,"dismissed":false}')
    if (!data.dismissed && data.views < 5) {
      setShowPromo(true)
      localStorage.setItem(key, JSON.stringify({ views: data.views + 1, dismissed: false }))
    }
  }, [])

  const dismissPromo = () => {
    setShowPromo(false)
    const PROMO_VERSION = 'v3'
    const key = `onrecord_promo_${PROMO_VERSION}`
    localStorage.setItem(key, JSON.stringify({ views: 5, dismissed: true }))
  }

  const goToSlide = useCallback((nextIndex: number) => {
    if (sliding) return
    setNextPromoIndex(nextIndex)
    setSliding(true)
    setTimeout(() => {
      setPromoIndex(nextIndex)
      setNextPromoIndex(null)
      setSliding(false)
    }, 500)
  }, [sliding])

  // Auto-slide: 8s per card, 12s pause after full cycle
  useEffect(() => {
    if (!showPromo) return
    const isLastCard = promoIndex === PROMO_CARDS.length - 1
    const delay = isLastCard ? 12000 : 8000

    promoTimerRef.current = setTimeout(() => {
      goToSlide((promoIndex + 1) % PROMO_CARDS.length)
    }, delay)

    return () => {
      if (promoTimerRef.current) clearTimeout(promoTimerRef.current)
    }
  }, [showPromo, promoIndex, goToSlide])

  const promo = PROMO_CARDS[promoIndex]

  const [trending, setTrending] = useState([
    { tag: '#OnRecordLaunch', posts: '12.5K' },
    { tag: '#AuthenticContent', posts: '8.9K' },
    { tag: '#LocalNews', posts: '15.2K' },
    { tag: '#CommunityFirst', posts: '6.1K' },
  ])

  useEffect(() => {
    loadSuggestions()
    loadTrending()
  }, [])

  const loadSuggestions = async () => {
    try {
      const result = await api.getUserSuggestions()
      if (result.success && result.suggestions.length > 0) {
        setSuggestions(result.suggestions.map((u: any) => ({
          id: u.id,
          name: u.name,
          username: u.username,
          avatar: u.avatar,
          verified: u.verified,
          isFollowing: false,
        })))
      }
    } catch {}
  }

  const loadTrending = async () => {
    try {
      const result = await api.getTrendingHashtags({ limit: 4 })
      if (result.success && result.hashtags?.length > 0) {
        setTrending(result.hashtags.map((h: any) => ({
          tag: `#${h.tag}`,
          posts: h.count >= 1000 ? `${(h.count / 1000).toFixed(1)}K` : `${h.count}`,
        })))
      }
    } catch {}
  }

  const handleFollow = async (userId: string) => {
    setSuggestions(prev => prev.map(u =>
      u.id === userId ? { ...u, isFollowing: !u.isFollowing } : u
    ))
    try {
      await api.followUser(userId)
    } catch {
      setSuggestions(prev => prev.map(u =>
        u.id === userId ? { ...u, isFollowing: !u.isFollowing } : u
      ))
    }
  }

  return (
    <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0 px-4 xl:px-6 py-2 h-screen sticky top-0 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'none' }}>
      {/* Search Bar */}
      <div className="sticky top-0 bg-white py-2 mb-4">
        <div className="relative">
          <Search className="absolute left-4 top-3 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                navigate(`/explore?q=${encodeURIComponent(searchQuery.trim())}`)
                setSearchQuery('')
              }
            }}
            className="search-input w-full pl-12 pr-4 py-3 rounded-full focus:outline-none transition-all text-gray-900 placeholder-gray-500"
          />
        </div>
      </div>

      {/* App Promo Card — auto-shuffling, shown to new users */}
      {showPromo && <div className="rounded-2xl border border-gray-200 p-4 mb-4 overflow-hidden relative" style={{ minHeight: '220px' }}>
        <button onClick={dismissPromo} className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors z-10">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        <div style={{ height: '170px', position: 'relative', overflow: 'hidden' }}>
          {/* Strip: both cards side by side, push together */}
          <div
            style={{
              display: 'flex',
              width: '200%',
              gap: '2px',
              transform: sliding ? 'translateX(calc(-50% - 1px))' : 'translateX(0)',
              transition: sliding ? 'transform 0.55s ease-in-out' : 'none',
            }}
          >
            {/* Current card */}
            <div style={{ width: '50%', flexShrink: 0 }}>
              {(() => { const c = PROMO_CARDS[promoIndex]; return (<>
                <div className="flex items-start gap-2 mb-2 pr-5">
                  <c.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: c.iconColor }} />
                  <h3 className="font-bold text-[15px] text-gray-900 leading-tight">{c.title}</h3>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-3 line-clamp-3">{c.body}</p>
                <div className="flex items-center gap-2">
                  <a href="https://apps.apple.com/app/on-record/id1534718618" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('ios')}>
                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-9" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.onrecord.live" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('android')}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Play Store" className="h-9" />
                  </a>
                </div>
              </>)})()}
            </div>
            {/* Next card — pushed in from right */}
            <div style={{ width: '50%', flexShrink: 0, borderLeft: '1px solid #e5e7eb' }}>
              {(() => { const c = PROMO_CARDS[nextPromoIndex !== null ? nextPromoIndex : (promoIndex + 1) % PROMO_CARDS.length]; return (<>
                <div className="flex items-start gap-2 mb-2 pr-5">
                  <c.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: c.iconColor }} />
                  <h3 className="font-bold text-[15px] text-gray-900 leading-tight">{c.title}</h3>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-3 line-clamp-3">{c.body}</p>
                <div className="flex items-center gap-2">
                  <a href="https://apps.apple.com/app/on-record/id1534718618" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('ios')}>
                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-9" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.onrecord.live" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('android')}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Play Store" className="h-9" />
                  </a>
                </div>
              </>)})()}
            </div>
          </div>
        </div>
        {/* Dots indicator */}
        <div className="flex justify-center gap-1.5 mt-3">
          {PROMO_CARDS.map((_, i) => (
            <button
              key={i}
              onClick={() => { if (i !== promoIndex) { if (promoTimerRef.current) clearTimeout(promoTimerRef.current); goToSlide(i) } }}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{ background: i === promoIndex ? '#1DA1F2' : '#D1D5DB' }}
            />
          ))}
        </div>
      </div>}

      {/* Trending Topics */}
      <div className="trending-section mb-4">
        <div className="p-4">
          <h2 className="font-bold text-xl mb-3 text-gray-900">Trending Now</h2>
        </div>
        {trending.map((item, index) => (
          <div key={index}
            onClick={() => navigate(`/explore?q=${encodeURIComponent(item.tag)}`)}
            className="trending-item py-3 px-4 cursor-pointer transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-sm text-gray-500">Trending</div>
                <div className="font-bold text-gray-900">{item.tag}</div>
                <div className="text-sm text-gray-500">{item.posts} posts</div>
              </div>
              <button className="text-gray-500 hover:text-primary transition-colors">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
        <button onClick={() => navigate('/explore')}
          className="p-4 text-primary hover:bg-gray-50 w-full text-left transition-colors">
          Show more
        </button>
      </div>

      {/* Who to Follow — Real suggestions */}
      <div className="who-to-follow-section">
        <div className="p-4">
          <h2 className="font-bold text-xl mb-3 text-gray-900">Who to follow</h2>
        </div>
        {suggestions.map((user) => (
          <div key={user.id}
            className="follow-suggestion-item flex items-center justify-between py-3 px-4 cursor-pointer transition-colors">
            <div className="flex items-center gap-3" onClick={() => navigate(`/profile/${user.id}`)}>
              <Avatar src={user.avatar} alt={user.name} size="md" />
              <div>
                <div className="font-bold flex items-center gap-1 text-gray-900 text-sm">
                  {user.name}
                  {user.verified && (
                    <svg className="w-4 h-4 text-onrecord-orange" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500">@{user.username}</div>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleFollow(user.id) }}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
                user.isFollowing
                  ? 'bg-white text-gray-900 border border-gray-300 hover:border-red-300 hover:text-red-600'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}>
              {user.isFollowing ? 'Following' : 'Follow'}
            </button>
          </div>
        ))}
        <button onClick={() => navigate('/explore')}
          className="p-4 text-primary hover:bg-gray-50 w-full text-left transition-colors">
          Show more
        </button>
      </div>

      {/* Footer */}
      <div className="mt-4 p-4">
        <div className="flex flex-wrap gap-x-3 gap-y-2 text-xs text-gray-500">
          <a href="/about" className="hover:underline">About</a>
          <a href="/terms" className="hover:underline">Terms</a>
          <a href="/privacy" className="hover:underline">Privacy</a>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          © {new Date().getFullYear()} on record
        </div>
      </div>
    </div>
  )
}
